import { isOwnPrivateColumn } from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'
import { RundownPermission } from '~shared/types'

// =============== TYPES

/**
 * What `getColumnAccess` needs to know about the viewer.
 *
 * @remarks
 *   Passed in rather than read here, so this module stays pure: no stores, no
 *   React, and free of the axios/firebase import chain behind
 *   `hasRundownPermission`.
 */
export type ColumnAccessContext = {
  /** Signed-in user's uid, or empty when signed out. */
  userId: string

  /** Usually `hasRundownPermission`. */
  hasPermission: (permission: RundownPermission) => boolean

  /** Columns the share link lets a partial editor edit (`?edit-columns=`). */
  editableColumnIds: readonly Column['id'][]
}

/**
 * Everything a column header and its cells decide from the viewer's access.
 */
export type ColumnAccess = {
  /** The column is the viewer's own private column. */
  isOwnPrivate: boolean

  /**
   * Full editor of the rundown. Not about this column: carried here so the
   * header reads one object, eg. for "Add column after" and "Copy column ID".
   */
  isEditor: boolean

  /**
   * A guest who may edit this column's cells only because the share link
   * allows it. Full editors are never counted, so the "(Editable)" hint only
   * shows where it tells the viewer something.
   */
  isGuestEditable: boolean

  /**
   * May edit the column's cells: own private column, full editor, or partial
   * editor of a column the share link allows.
   */
  canEditCells: boolean

  /**
   * May write the column document for everyone: width, delete (or clear for
   * the own private column) and pushing a reorder to the server.
   */
  canWriteColumn: boolean

  /**
   * May change the column's name and type. A private column is never
   * configurable, not even by its owner.
   */
  canConfigure: boolean
}

// =============== FUNCTIONS

/**
 * Determine the viewer's access to a column.
 */
export function getColumnAccess(
  column: Pick<Column, 'id' | 'privateUid'>,
  ctx: ColumnAccessContext,
): ColumnAccess {
  const isOwnPrivate = isOwnPrivateColumn(column, ctx.userId)
  const isEditor = ctx.hasPermission(RundownPermission.EDIT)
  const isPartialEditor = ctx.hasPermission(RundownPermission.EDIT_PARTIAL)
  const isLinkEditable =
    isPartialEditor && ctx.editableColumnIds.includes(column.id)

  return {
    isOwnPrivate,
    isEditor,
    isGuestEditable: !isEditor && isLinkEditable,
    canEditCells: isOwnPrivate || isEditor || isLinkEditable,
    canWriteColumn: isEditor || isOwnPrivate,
    canConfigure: isEditor && !isOwnPrivate,
  }
}
